import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { getUser } from 'redux/selectors';
import { updateUI } from 'redux/actions/ui';
import buildAuthorization from 'common/utils/buildAuthorization';
import settings from 'common/configs/setting';
import useS3MultipartUpload from 'hooks/useS3MultipartUpload';
import './ImportGmailDialog.css';

const MAX_FILE_SIZE = 10 * 1024 * 1024 * 1024; // 10 GB

const getApiBaseUrl = () => {
    const stored = localStorage.getItem('API_URL') || '';
    return stored.replace(/\/+$/, '') + settings.api;
};

const STATUS = {
    IDLE: 'idle',
    GETTING_URL: 'getting_url',
    UPLOADING: 'uploading',
    CONFIRMING: 'confirming',
    POLLING: 'polling',
    DONE: 'done',
    ERROR: 'error'
};

// Display mode for the modal
const DISPLAY = {
    FULL: 'full', // Normal modal visible
    MESSAGE: 'message', // Brief "processing in background" message
    HIDDEN: 'hidden' // Renders null — dialog alive but invisible (polling continues)
};

const POLL_INTERVAL_MS = 3000;

const getFileFormat = (file) => {
    if (!file) {
        return null;
    }
    const ext = file.name.split('.').pop().toLowerCase();
    return ext === 'mbox' || ext === 'eml' ? ext : null;
};

const ImportGmailDialog = ({ close, journal }) => {
    const user = useSelector(getUser);
    const reduxDispatch = useDispatch();
    const fileInputRef = useRef(null);
    const pollTimerRef = useRef(null);
    const messageTimerRef = useRef(null);

    const [selectedFile, setSelectedFile] = useState(null);
    const [isDragging, setIsDragging] = useState(false);
    const [status, setStatus] = useState(STATUS.IDLE);
    const [errorMsg, setErrorMsg] = useState('');
    const [pendingFile, setPendingFile] = useState(null);
    const [displayMode, setDisplayMode] = useState(DISPLAY.FULL);

    const journalId = journal?.id;
    const authHeader = buildAuthorization(user);
    const isProcessingRef = useRef(false);

    // Use real S3 upload for email imports
    const {
        uploadToS3Multipart,
        cancelUpload,
        resetUploadState,
        formatBytes,
        formatDuration,
        uploadProgress,
        uploadSpeed,
        uploadEta
    } = useS3MultipartUpload();

    // Safety cleanup on unmount: clear timers and in-progress flag
    useEffect(() => {
        return () => {
            clearInterval(pollTimerRef.current);
            clearTimeout(messageTimerRef.current);
            cancelUpload();
            resetUploadState();
            reduxDispatch(updateUI({ gmailImportInProgress: false }));
        };
    }, [reduxDispatch, cancelUpload, resetUploadState]);

    // ── Step 1: pre-signed upload URL ────────────────────────────────────────
    const initMultipartUpload = useCallback(
        async (file, fileFormat) => {
            const params = new URLSearchParams({
                filename: file.name,
                file_format: fileFormat,
                file_size: file.size.toString()
            });
            const url = `${getApiBaseUrl()}/journal/${journalId}/email-imports/multipart-upload-init/?${params}`;
            try {
                const res = await fetch(url, {
                    method: 'GET',
                    headers: { Authorization: authHeader }
                });
                if (!res.ok) {
                    const txt = await res.text();
                    throw new Error(
                        `Could not init multipart upload (HTTP ${res.status}): ${txt}`
                    );
                }
                return await res.json();
            } catch (err) {
                throw new Error(
                    `Network error getting upload URL: ${err.message}`
                );
            }
        },
        [journalId, authHeader]
    );

    // ── Step 2: upload directly to S3 with REAL progress tracking ───────────
    const uploadToS3 = useCallback(
        async (file, fileFormat, uploadData) => {
            // Use the real progress tracking from the hook
            const uploadConfig = { ...uploadData, use_multipart: true };
            const result = await uploadToS3Multipart(file, uploadConfig);
            return result;
        },
        [uploadToS3Multipart]
    );

    // ── Step 3: confirm upload ───────────────────────────────────────────────
    const confirmUpload = useCallback(
        async (uploadId, s3Key, partEtags, fileFormat) => {
            const url = `${getApiBaseUrl()}/journal/${journalId}/email-imports/multipart-upload-complete/`;
            try {
                const res = await fetch(url, {
                    method: 'POST',
                    headers: {
                        Authorization: authHeader,
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({
                        upload_id: uploadId,
                        s3_key: s3Key,
                        part_etags: partEtags,
                        file_format: fileFormat
                    })
                });
                if (!res.ok) {
                    const txt = await res.text();
                    throw new Error(
                        `Confirm failed (HTTP ${res.status}): ${txt}`
                    );
                }
                return await res.json();
            } catch (err) {
                throw new Error(`Exception during confirm: ${err.message}`);
            }
        },
        [journalId, authHeader]
    );

    // ── Step 4: poll job ─────────────────────────────────────────────────────
    // When polling starts:
    //   1. Show brief "Upload successful, processing in background…" message
    //   2. After 2.5s, hide modal entirely (renders null) — polling continues
    //   3. On complete → dispatch result to Redux → sidebar shows popup → self-close
    const startPolling = useCallback(
        (jobId) => {
            clearInterval(pollTimerRef.current);
            setStatus(STATUS.POLLING);

            // Signal sidebar: disable button + show spinner
            reduxDispatch(
                updateUI({ gmailImportInProgress: true, gmailJobResult: null })
            );

            // Store in local storage to allow fetching across refreshes
            localStorage.setItem(
                'activeGmailJob',
                JSON.stringify({ jobId, journalId })
            );

            // Show brief message then go hidden
            setDisplayMode(DISPLAY.MESSAGE);
            messageTimerRef.current = setTimeout(() => {
                setDisplayMode(DISPLAY.HIDDEN);
            }, 2500);

            pollTimerRef.current = setInterval(async () => {
                const statusUrl = `${getApiBaseUrl()}/journal/${journalId}/email-imports/${jobId}/`;
                try {
                    const res = await fetch(statusUrl, {
                        method: 'GET',
                        headers: { Authorization: authHeader }
                    });
                    if (!res.ok) {
                        throw new Error(`Poll failed (HTTP ${res.status})`);
                    }
                    const data = await res.json();
                    const st = (data.status || '').toUpperCase();

                    if (st === 'DONE' || st === 'FAILED') {
                        clearInterval(pollTimerRef.current);
                        clearTimeout(messageTimerRef.current);

                        const result = {
                            journalId,
                            success:
                                data.success_count ?? data.emails_imported ?? 0,
                            skipped: data.skipped_count ?? 0,
                            failed: data.failed_count ?? 0,
                            isError: st === 'FAILED',
                            errorDetails: data.error_details || '',
                            skippedReasons: data.skipped_reasons || '[]',
                            failedReasons: data.failed_reasons || '[]'
                        };

                        // Push result to Redux for sidebar popup, clear in-progress
                        reduxDispatch(
                            updateUI({
                                gmailImportInProgress: false,
                                gmailJobResult: result
                            })
                        );

                        // Self-close the dialog (component unmounts cleanly — polling done)
                        if (close) {
                            close();
                        }
                    }
                } catch (err) {
                    clearInterval(pollTimerRef.current);
                    clearTimeout(messageTimerRef.current);
                    reduxDispatch(
                        updateUI({
                            gmailImportInProgress: false,
                            gmailJobResult: {
                                success: 0,
                                skipped: 0,
                                failed: 0,
                                isError: true,
                                errorDetails: err.message || 'Unknown error'
                            }
                        })
                    );
                    if (close) {
                        close();
                    }
                }
            }, POLL_INTERVAL_MS);
        },
        [journalId, authHeader, close, reduxDispatch]
    );

    // ── Main upload flow (auto-triggered when pendingFile is set) ────────────
    useEffect(() => {
        if (!pendingFile) {
            return;
        }

        const fileToUpload = pendingFile;
        setPendingFile(null); // Consume immediately to prevent React dependency re-triggers

        const run = async () => {
            isProcessingRef.current = true;
            const fileFormat = getFileFormat(fileToUpload);
            if (!fileFormat) {
                setErrorMsg('Only .mbox and .eml files are supported.');
                setStatus(STATUS.ERROR);
                isProcessingRef.current = false;
                return;
            }
            if (!journalId) {
                setErrorMsg(
                    'No active journal found. Please close and try again.'
                );
                setStatus(STATUS.ERROR);
                isProcessingRef.current = false;
                return;
            }

            try {
                setStatus(STATUS.GETTING_URL);
                setErrorMsg('');

                // Use multipart-upload for all email files
                const uploadData = await initMultipartUpload(
                    fileToUpload,
                    fileFormat
                );
                setStatus(STATUS.UPLOADING);
                const uploadResult = await uploadToS3(
                    fileToUpload,
                    fileFormat,
                    uploadData
                );

                setStatus(STATUS.CONFIRMING);
                const confirmData = await confirmUpload(
                    uploadResult.uploadId,
                    uploadResult.s3_key,
                    uploadResult.partETags,
                    fileFormat
                );

                const jobId = confirmData?.id || confirmData?.job_id;
                if (!jobId) {
                    throw new Error('No job ID returned from server.');
                }
                startPolling(jobId);
            } catch (err) {
                setErrorMsg(err.message || 'Upload failed. Please try again.');
                setStatus(STATUS.ERROR);
            } finally {
                isProcessingRef.current = false;
            }
        };

        run();
    }, [
        pendingFile,
        journalId,
        initMultipartUpload,
        uploadToS3,
        confirmUpload,
        startPolling
    ]);

    // ── File pick / drop ─────────────────────────────────────────────────────
    const handleFile = useCallback((file) => {
        if (!file || isProcessingRef.current) {
            return;
        }
        const fmt = getFileFormat(file);
        if (!fmt) {
            setErrorMsg(
                'Unsupported format. Only .mbox and .eml files are accepted.'
            );
            setStatus(STATUS.ERROR);
            return;
        }
        if (file.size > MAX_FILE_SIZE) {
            setErrorMsg(
                'File exceeds 10GB limit. Please upload a smaller file.'
            );
            setStatus(STATUS.ERROR);
            return;
        }
        setSelectedFile(file);
        setStatus(STATUS.IDLE);
        setErrorMsg('');
        setPendingFile(file);
    }, []);

    const onSelectFile = useCallback(() => {
        if (fileInputRef.current) {
            fileInputRef.current.click();
        }
    }, []);

    const onFileChange = useCallback((e) => handleFile(e.target.files[0]), [
        handleFile
    ]);

    const onDragOver = useCallback((e) => {
        e.preventDefault();
        setIsDragging(true);
    }, []);

    const onDragLeave = useCallback(() => setIsDragging(false), []);

    const onDrop = useCallback(
        (e) => {
            e.preventDefault();
            setIsDragging(false);
            handleFile(e.dataTransfer.files[0]);
        },
        [handleFile]
    );

    const onDone = useCallback(() => {
        clearInterval(pollTimerRef.current);
        clearTimeout(messageTimerRef.current);
        if (close) {
            close();
        }
    }, [close]);

    const onRetry = useCallback(() => {
        if (selectedFile) {
            setStatus(STATUS.IDLE);
            setErrorMsg('');
            setDisplayMode(DISPLAY.FULL);
            setPendingFile(selectedFile);
        }
    }, [selectedFile]);

    // ── Render: HIDDEN — dialog stays mounted so polling can finish ──────────
    if (displayMode === DISPLAY.HIDDEN) {
        return null;
    }

    // ── Render: MESSAGE — brief "processing in background" overlay ───────────
    if (displayMode === DISPLAY.MESSAGE) {
        return (
            <div className="modal-frame ig-overlay">
                <div className="modal-box ig-modal-box ig-modal-box--message">
                    <div className="ig-bg-message">
                        <div className="ig-bg-message__icon">✓</div>
                        <h3 className="ig-bg-message__title">
                            Upload Successful!
                        </h3>
                        <p className="ig-bg-message__body">
                            Your file has been uploaded. Emails are now being
                            processed in the background &mdash; you can continue
                            using the app. We&apos;ll notify you here when
                            it&apos;s done.
                        </p>
                    </div>
                </div>
            </div>
        );
    }

    // ── Render: FULL — normal modal ───────────────────────────────────────────
    const isProcessing = [
        STATUS.GETTING_URL,
        STATUS.UPLOADING,
        STATUS.CONFIRMING
    ].includes(status);

    const stepLabel =
        {
            [STATUS.IDLE]: '',
            [STATUS.GETTING_URL]: 'Preparing secure upload…',
            [STATUS.UPLOADING]: `Uploading file… ${uploadProgress}%`,
            [STATUS.CONFIRMING]: 'Confirming with server…',
            [STATUS.DONE]: '',
            [STATUS.ERROR]: ''
        }[status] || '';

    return (
        <div className="modal-frame ig-overlay">
            <div className="modal-box ig-modal-box">
                <div className="ig-header">
                    <h2 className="ig-title">Upload Gmail .Mbox File</h2>
                </div>

                <div className="ig-content">
                    <p className="ig-instructions">
                        You can import your mission emails into your Mission
                        JRNL by uploading an .mbox file. It will only accept
                        emails that fall within 42 months of your mission start
                        date. Any emails beyond that will be excluded. Each
                        email will be converted to an individual journal entry
                        and include any attached photos. Video, audio files, or
                        other documents or file types will not be included.
                    </p>

                    {/* ── Status Banner ── */}
                    {status === STATUS.ERROR && (
                        <div className="ig-banner ig-banner--error">
                            <span role="img" aria-label="error">
                                ⚠
                            </span>
                            <span>{errorMsg}</span>
                            {selectedFile && (
                                <button
                                    className="ig-banner__retry"
                                    onClick={onRetry}>
                                    Retry
                                </button>
                            )}
                        </div>
                    )}
                    {isProcessing && stepLabel && (
                        <div className="ig-banner ig-banner--info">
                            <div className="ig-spinner" />
                            <span>{stepLabel}</span>
                        </div>
                    )}

                    {/* ── Drop Zone ── */}
                    <div
                        className={`ig-dropzone ${
                            isDragging ? 'ig-dropzone--dragging' : ''
                        } ${isProcessing ? 'ig-dropzone--busy' : ''}`}
                        onDragOver={onDragOver}
                        onDragLeave={onDragLeave}
                        onDrop={onDrop}>
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept=".mbox,.eml"
                            style={{ display: 'none' }}
                            onChange={onFileChange}
                        />

                        {/* GETTING_URL — preparing */}
                        {status === STATUS.GETTING_URL && (
                            <div className="ig-dropzone-status">
                                <div className="ig-dropzone-status__spinner" />
                                <p className="ig-dropzone-status__label">
                                    Preparing secure upload…
                                </p>
                                <p className="ig-dropzone-status__sub">
                                    Getting a secure upload URL from the server
                                </p>
                            </div>
                        )}

                        {/* UPLOADING — progress bar with speed & ETA */}
                        {status === STATUS.UPLOADING && (
                            <div className="ig-progress-wrap">
                                {/* Progress bar */}
                                <div className="ig-progress-bar">
                                    <div
                                        className="ig-progress-bar__fill"
                                        style={{ width: `${uploadProgress}%` }}
                                    />
                                </div>

                                {/* Progress percentage */}
                                <div className="ig-progress-header">
                                    <span className="ig-progress-pct">
                                        {uploadProgress}%
                                    </span>
                                    <span className="ig-progress-eta">
                                        {uploadSpeed > 0
                                            ? `${formatDuration(
                                                  uploadEta
                                              )} remaining`
                                            : 'Calculating…'}
                                    </span>
                                </div>

                                {/* Speed & file info */}
                                <div className="ig-progress-stats">
                                    <div className="ig-stat">
                                        <span className="ig-stat__label">
                                            Speed:
                                        </span>
                                        <span className="ig-stat__value">
                                            {uploadSpeed} Mbps
                                        </span>
                                    </div>
                                    {selectedFile && (
                                        <div className="ig-stat">
                                            <span className="ig-stat__label">
                                                File size:
                                            </span>
                                            <span className="ig-stat__value">
                                                {formatBytes(selectedFile.size)}
                                            </span>
                                        </div>
                                    )}
                                </div>

                                {/* Cancel button */}
                                <button
                                    type="button"
                                    className="ig-btn-cancel"
                                    style={{ pointerEvents: 'auto' }}
                                    onClick={() => {
                                        cancelUpload();
                                        if (close) {
                                            close();
                                        }
                                    }}>
                                    Cancel Upload
                                </button>
                            </div>
                        )}

                        {/* CONFIRMING — waiting for server acknowledgement */}
                        {status === STATUS.CONFIRMING && (
                            <div className="ig-dropzone-status">
                                <div className="ig-dropzone-status__spinner" />
                                <p className="ig-dropzone-status__label">
                                    Confirming with server…
                                </p>
                                <p className="ig-dropzone-status__sub">
                                    Registering the uploaded file for processing
                                </p>
                            </div>
                        )}

                        {!isProcessing && (
                            <>
                                <p className="ig-dropzone-title">
                                    Drop Files Here to Upload
                                </p>

                                {selectedFile && (
                                    <p className="ig-selected-file">
                                        <span role="img" aria-label="file">
                                            📎
                                        </span>{' '}
                                        {selectedFile.name}
                                    </p>
                                )}

                                <button
                                    className="btn btn-gradient ig-select-btn"
                                    onClick={onSelectFile}>
                                    Select File
                                </button>

                                <div className="ig-dropzone-info">
                                    <p>Maximum upload size: 10 gb</p>
                                    <p>Formats: .mbox &amp; .eml</p>
                                </div>
                            </>
                        )}
                    </div>
                </div>

                <div className="ig-footer">
                    <button
                        className="btn btn-outline ig-done-btn"
                        onClick={onDone}
                        disabled={isProcessing}>
                        Done
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ImportGmailDialog;
